'use client'

import { useState, useMemo } from 'react'
import { Button } from '@/ui/button'
import { DownloadSimpleIcon } from '@phosphor-icons/react'
import { SpinnerGapIcon } from '@phosphor-icons/react'
import { exportBackup, downloadBackup } from './backup'
import { useDocumentView, useDocumentStatus } from './use-document'

type ExportState = 'idle' | 'exporting' | 'success' | 'failed'

export function ExportDataButton() {
  const doc = useDocumentView()
  const ready = useDocumentStatus()
  const [state, setState] = useState<ExportState>('idle')
  const [error, setError] = useState<string>('')

  const entryCount = useMemo(() => {
    if (!doc) return 0
    return Object.keys(doc.entries).length
  }, [doc])

  const handleExport = () => {
    if (!doc) return

    setState('exporting')
    setError('')

    try {
      const backup = exportBackup(doc)
      downloadBackup(backup)
      setState('success')
      setTimeout(() => setState('idle'), 1500)
    } catch (err) {
      setState('failed')
      setError(err instanceof Error ? err.message : 'Export failed')
    }
  }

  if (!ready) {
    return (
      <Button disabled variant="outline" size="lg" className="flex-1">
        <SpinnerGapIcon className="mr-2 h-4 w-4 animate-spin" />
        Loading...
      </Button>
    )
  }

  if (state === 'exporting') {
    return (
      <Button disabled variant="outline" size="lg" className="flex-1">
        <SpinnerGapIcon className="mr-2 h-4 w-4 animate-spin" />
        Exporting...
      </Button>
    )
  }

  if (state === 'success') {
    return (
      <Button disabled variant="outline" size="lg" className="flex-1">
        Export complete
      </Button>
    )
  }

  if (state === 'failed') {
    return (
      <Button
        onClick={handleExport}
        variant="outline"
        size="lg"
        className="flex-1"
        aria-label="Retry export"
      >
        <DownloadSimpleIcon className="mr-2 h-4 w-4" />
        Retry Export {error ? `(${error})` : ''}
      </Button>
    )
  }

  return (
    <Button
      onClick={handleExport}
      variant="outline"
      size="lg"
      className="flex-1"
      aria-label="Export data"
    >
      <DownloadSimpleIcon className="mr-2 h-4 w-4" />
      Export Data ({entryCount} entries)
    </Button>
  )
}
